import { extract } from '../extractors/unlock';
import { fmtUSD, fmtPct } from '../extractors/_shared';
import { judgeUnlockPressure30d, judgeUnlockPressure180d, judgeTeamShare } from '../judgments/unlockPressure';

const txt = (lang, zh, en) => (lang === 'en' ? en : zh);

// 5 类分配 + 颜色（与 PDF 报告 src/App.jsx allocColor 一致）
// team/contributor/advisor/founder → 红（团队归属，最敏感）
// investor/vc/strategic            → 橙（投资方归属）
// community/grant/airdrop/public   → 绿（社区/公共归属）
// liquidity/ecosystem/reserve/treasury → 蓝（生态/储备）
// foundation/budget                 → 紫（基金会）
// 其他                              → 灰
function categorizeAllocation(name) {
  const n = (name || '').toLowerCase();
  if (/team|contributor|advisor|founder/.test(n))    return { key: 'team',       color: '#dc2626', labelZh: '团队',     labelEn: 'Team' };
  if (/investor|vc|strategic/.test(n))                return { key: 'investor',   color: '#f97316', labelZh: '投资方',   labelEn: 'Investors' };
  if (/community|grant|airdrop|public/.test(n))       return { key: 'community',  color: '#059669', labelZh: '社区',     labelEn: 'Community' };
  if (/liquidity|ecosystem|reserve|treasury/.test(n)) return { key: 'ecosystem',  color: '#2563eb', labelZh: '生态/储备', labelEn: 'Ecosystem' };
  if (/foundation|budget/.test(n))                    return { key: 'foundation', color: '#8b5cf6', labelZh: '基金会',   labelEn: 'Foundation' };
  return { key: 'other', color: '#6b7280', labelZh: '其他', labelEn: 'Other' };
}

export function getUnlockEvidence(project, a, lang = 'zh') {
  const d = extract(project, a);
  if (!d) return [];
  const rows = [];
  if (d.next30dAmount != null) {
    const j = judgeUnlockPressure30d(d.next30dRatio);
    const valStr = d.next30dRatio != null
      ? `${j?.emoji || ''} ${fmtUSD(d.next30dAmount)} (${fmtPct(d.next30dRatio, 2)})`.trim()
      : `${j?.emoji || ''} ${fmtUSD(d.next30dAmount)}`.trim();
    rows.push({
      label: txt(lang, '未来30日解锁量', 'Next 30d Unlock'),
      val: valStr, source: 'Surf', positive: j?.positive ?? null,
    });
  }
  if (d.teamShare != null) {
    const j = judgeTeamShare(d.teamShare);
    rows.push({
      label: txt(lang, '团队/投资方占比', 'Team/Investor Share'),
      val: `${j?.emoji || ''} ${fmtPct(d.teamShare, 0)}`.trim(),
      source: 'Surf', positive: j?.positive ?? null,
    });
  }
  if (d.eventsNext90dCount > 0) {
    rows.push({
      label: txt(lang, '未来90日解锁事件数', '90d Unlock Events'),
      val: `${d.eventsNext90dCount}`, source: 'Surf',
    });
  }
  return rows;
}

export function UnlockCard({ project, a, lang = 'zh', S, C }) {
  const d = extract(project, a);
  if (!d) return null;
  const allocSorted = [...d.allocations].sort((a, b) => (b?.amount || 0) - (a?.amount || 0));
  // 过滤掉 delta = 0 的"无变化锚点"事件 — 这些在 cumulative → delta 转换后
  // 不代表真实未来解锁，对用户没意义，在 table 里显示 $0 反而误导。
  const meaningfulEvents = (d.events || []).filter(e => (e?.totalAmount || 0) > 0);
  const noUpcomingUnlocks = meaningfulEvents.length === 0;
  return (
    <div style={S?.card || { background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 12 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: C?.muted || '#6b7280', marginBottom: 10, textTransform: 'uppercase', display: 'flex', justifyContent: 'space-between' }}>
        <span>🔓 {txt(lang, '详细代币解锁日程', 'Detailed Unlock Schedule')}</span>
        <span style={{ fontWeight: 400, fontSize: 9, color: '#94a3b8' }}>Surf</span>
      </div>
      {d.next30dAmount > 0 ? (
        <div style={{ marginBottom: 12, padding: '8px 10px', background: '#fef3c7', borderRadius: 6, border: '1px solid #fde68a' }}>
          <div style={{ fontSize: 10, color: '#92400e', fontWeight: 700 }}>
            ⚠️ {txt(lang, '未来30日解锁量', 'Next 30 Days Unlock')}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>{fmtUSD(d.next30dAmount)}{d.next30dRatio != null ? ` · ${fmtPct(d.next30dRatio, 2)}` : ''}</div>
        </div>
      ) : noUpcomingUnlocks && (
        <div style={{ marginBottom: 12, padding: '8px 10px', background: '#ecfdf5', borderRadius: 6, border: '1px solid #a7f3d0' }}>
          <div style={{ fontSize: 10, color: '#065f46', fontWeight: 700 }}>
            ✅ {txt(lang, '未来30日解锁量', 'Next 30 Days Unlock')}
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: '#065f46' }}>
            $0.00 · 0.00%
          </div>
          <div style={{ fontSize: 10, color: '#047857', marginTop: 3 }}>
            {txt(lang,
              '项目代币已全部解锁完成，无未来抛压',
              'Token fully unlocked, no future emission pressure')}
          </div>
        </div>
      )}
      {allocSorted.length > 0 && (() => {
        const total = allocSorted.reduce((s, a) => s + (a?.amount || 0), 0);
        // 5 类归总（用 categorizeAllocation 一致映射 PDF 报告颜色）
        const groups = {};
        allocSorted.forEach(a => {
          const cat = categorizeAllocation(a?.name);
          if (!groups[cat.key]) groups[cat.key] = { ...cat, amount: 0 };
          groups[cat.key].amount += a?.amount || 0;
        });
        const groupArr = Object.values(groups)
          .filter(g => g.amount > 0)
          .sort((a, b) => b.amount - a.amount);

        // SVG donut 数学：r=78, stroke-width=42 → 圆环外径 99，内径 57
        // 周长 ≈ 2π × 78 = 490
        const CIRC = 490;
        let cumOffset = 0;
        const slices = groupArr.map(g => {
          const pct = g.amount / total;
          const length = CIRC * pct;
          const slice = { color: g.color, length, offset: -cumOffset };
          cumOffset += length;
          return slice;
        });

        // 高风险信号：team + investor 占比 > 50%
        const teamPlusVcAmount = (groups.team?.amount || 0) + (groups.investor?.amount || 0);
        const teamPlusVcPct = total > 0 ? (teamPlusVcAmount / total) * 100 : 0;
        const isHighRisk = teamPlusVcPct > 50;

        return (
          <div style={{ marginBottom: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C?.muted || '#6b7280', textTransform: 'uppercase' }}>
                {txt(lang, '分配类别（全周期总量）', 'Allocation Breakdown')}
              </div>
              <div style={{ fontSize: 9, color: C?.muted || '#9ca3af' }}>
                {txt(lang, '占总分配比例', '% of total allocation')}
              </div>
            </div>

            {/* Donut + 图例：双栏布局 */}
            <div style={{ display: 'grid', gridTemplateColumns: '160px 1fr', gap: 16, alignItems: 'center', marginBottom: 12 }}>
              <svg viewBox="0 0 200 200" style={{ width: '100%', height: 'auto' }} role="img" aria-label={txt(lang, '代币分配饼图', 'Token allocation pie chart')}>
                <circle cx="100" cy="100" r="78" fill="none" stroke={C?.faint || '#e5e7eb'} strokeWidth="42" />
                {slices.map((s, i) => (
                  <circle
                    key={i}
                    cx="100" cy="100" r="78"
                    fill="none"
                    stroke={s.color}
                    strokeWidth="42"
                    strokeDasharray={`${s.length} ${CIRC - s.length}`}
                    strokeDashoffset={s.offset}
                    transform="rotate(-90 100 100)"
                  />
                ))}
                <circle cx="100" cy="100" r="50" fill={C?.bg || '#fff'} />
                <text x="100" y="93" textAnchor="middle" fontSize="10" fill={C?.muted || '#6b7280'}>
                  {txt(lang, '总分配', 'Total')}
                </text>
                <text x="100" y="111" textAnchor="middle" fontSize="14" fontWeight="600" fill={C?.text || '#111'}>
                  {fmtUSD(total)}
                </text>
              </svg>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {groupArr.map((g, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ width: 9, height: 9, borderRadius: 2, background: g.color }} />
                      <span style={{ color: C?.text || '#111', fontWeight: 500 }}>{txt(lang, g.labelZh, g.labelEn)}</span>
                    </span>
                    <span style={{ fontFamily: 'monospace', color: g.color, fontWeight: 600 }}>
                      {((g.amount / total) * 100).toFixed(0)}%
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* 高风险警告条 */}
            {isHighRisk && (
              <div style={{ background: '#fef2f2', borderLeft: '3px solid #dc2626', borderRadius: '0 4px 4px 0', padding: '8px 12px', marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: '#991b1b', lineHeight: 1.5 }}>
                  <strong>⚠️ {txt(lang, '团队 + 投资方占比', 'Team + Investor share')} {teamPlusVcPct.toFixed(0)}%</strong> — {txt(lang, '高于 50% 阈值，警惕未来抛压', 'above 50% threshold, watch for sell pressure')}
                </div>
              </div>
            )}

            {/* 折叠明细：默认收起 */}
            <details style={{ fontSize: 11, color: C?.muted || '#6b7280' }}>
              <summary style={{ cursor: 'pointer', padding: '4px 0', userSelect: 'none' }}>
                {txt(lang, `展开 ${allocSorted.length} 个具体分配类别 →`, `Expand ${allocSorted.length} individual allocations →`)}
              </summary>
              <div style={{ marginTop: 10 }}>
                {allocSorted.slice(0, 8).map((a, i) => {
                  const amt = a?.amount || 0;
                  const pctTotal = total > 0 ? (amt / total) * 100 : 0;
                  const cat = categorizeAllocation(a?.name);
                  return (
                    <div key={i} style={{ marginBottom: 6 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 2 }}>
                        <span style={{ color: cat.color, fontWeight: 600 }}>{a?.name}</span>
                        <span style={{ color: C?.muted || '#6b7280', fontFamily: 'monospace' }}>
                          {fmtUSD(amt)} <span style={{ color: cat.color, fontWeight: 700, marginLeft: 4 }}>{pctTotal.toFixed(1)}%</span>
                        </span>
                      </div>
                      <div style={{ height: 5, background: C?.faint || '#f3f4f6', borderRadius: 3, overflow: 'hidden' }}>
                        <div style={{ width: `${pctTotal}%`, height: '100%', background: cat.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </details>
          </div>
        );
      })()}
      <div style={{ fontSize: 10, fontWeight: 700, color: C?.muted || '#6b7280', marginBottom: 8, textTransform: 'uppercase' }}>
        {txt(lang, '即将到来的解锁事件', 'Upcoming Unlock Events')}
      </div>
      {noUpcomingUnlocks ? (
        <div style={{ padding: '12px', textAlign: 'center', fontSize: 11, color: C?.muted || '#6b7280', background: '#f9fafb', borderRadius: 6 }}>
          {txt(lang,
            '暂无未来解锁事件 — 项目可能已完成全部释放，或 Surf 数据覆盖不全',
            'No upcoming unlock events — token may be fully released, or Surf data incomplete')}
        </div>
      ) : (
        <div style={{ maxHeight: 360, overflowY: 'auto' }}>
          <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
            <thead><tr style={{ background: '#f9fafb', color: C?.muted || '#6b7280', position: 'sticky', top: 0 }}>
              <th style={{ padding: '4px 6px', textAlign: 'left' }}>{txt(lang, '日期', 'Date')}</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>{txt(lang, '剩余天', 'In Days')}</th>
              <th style={{ padding: '4px 6px', textAlign: 'right' }}>{txt(lang, '解锁量', 'Amount')}</th>
              <th style={{ padding: '4px 6px', textAlign: 'left' }}>{txt(lang, '分配', 'Allocations')}</th>
            </tr></thead>
            <tbody>
              {meaningfulEvents.slice(0, 20).map((e, i) => {
                const urgent = e?.daysFromNow >= 0 && e?.daysFromNow <= 30;
                return (
                  <tr key={i} style={{ borderBottom: `1px solid ${C?.faint || '#f3f4f6'}`, background: urgent ? '#fef9c3' : 'transparent' }}>
                    <td style={{ padding: '4px 6px', fontFamily: 'monospace', fontSize: 10 }}>{e?.date}</td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', color: urgent ? '#dc2626' : (C?.muted || '#6b7280'), fontWeight: urgent ? 700 : 400 }}>
                      {e?.daysFromNow < 0 ? '—' : `${e?.daysFromNow}d`}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'right', fontWeight: 600 }}>{fmtUSD(e?.totalAmount)}</td>
                    <td style={{ padding: '4px 6px', fontSize: 10, color: C?.muted || '#6b7280' }}>
                      {(e?.allocations || []).slice(0, 3).map(a => a?.name).filter(Boolean).join(', ') || '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
